import create from "zustand";
import { produce } from "immer";

import { useAccountStore } from "./account.js";
import {
  ENVIRONMENT_TYPES,
  FLOW_STATUS,
  LEFTNAV_ACTION,
  PRODUCTS,
  FLOW_TYPE
} from "../../constants/index.js";
import { useUserStore } from "../stores/user.js";
import { getFilterPayload } from "../utils/index.js";
import { DataService } from "../services/index.js";

export const FLOW_FILTER = {
  Filter: {
    LHSField: "Type",
    RHSValue: `${FLOW_TYPE.PROCESS},${FLOW_TYPE.CASE},${FLOW_TYPE.APPLICATION}`,
    Operator: "PART_OF"
  }
};

const INITIAL_STORE = {
  flowList: new Map(), // To store all the flows in the account
  explorerFlows: new Map(), // To store all explorer flows
  explorerFilteredFlows: [],
  createAllowedFlows: [],
  templateAllowedFlows: [],
  archivedFlows: [],
  isExplorerFlowsLoading: false
};

const fetchPermissibleList = permissibleFlowsSingletonFactory();

const createWithImmer = (storeCreator) =>
  create((originalSet, originalGet) =>
    storeCreator((fn) => originalSet(produce(fn)), originalGet)
  );

const useGlobalStore = createWithImmer((setState, getState) => ({
  ...INITIAL_STORE,
  syncLeftNav: (action, flow) => {
    if (flow?._id) {
      const _flowList = new Map(getState().explorerFlows);
      switch (action) {
        case LEFTNAV_ACTION.ACTIVATE:
          if (
            flow?.Status !== FLOW_STATUS.ARCHIVED &&
            flow?.Permissions?.length > 0
          ) {
            let newFlowList = new Map();
            newFlowList.set(flow._id, flow);
            if (_flowList.has(flow._id)) {
              _flowList.delete(flow._id);
            }
            for (const [key, value] of _flowList.entries()) {
              newFlowList.set(key, value);
            }
            setState(() => {
              return { explorerFlows: newFlowList };
            });
          }
          break;
        case LEFTNAV_ACTION.UPDATE:
          if (_flowList.has(flow._id)) {
            _flowList.set(flow._id, { ..._flowList.get(flow._id), ...flow });
            setState(() => {
              return { explorerFlows: _flowList };
            });
          }
          break;
        case LEFTNAV_ACTION.ADD:
          if (!_flowList.has(flow._id)) {
            _flowList.set(flow._id, { ..._flowList.get(flow._id), ...flow });
            setState(() => {
              return { explorerFlows: _flowList };
            });
          }
          break;
        case LEFTNAV_ACTION.DELETE:
          if (_flowList.has(flow._id)) {
            _flowList.delete(flow._id);
            setState(() => {
              return { explorerFlows: _flowList };
            });
          }
          break;
        default:
          return null;
      }
    }
  },

  getFlow(flowId) {
    let promise = async function getFlows() {
      let flow =
        getState().explorerFlows.get(flowId) || getState().flowList.get(flowId);

      if (flow) return Promise.resolve(flow);

      return fetchPermissibleList()
        .then(() => {
          return Promise.resolve(getState().flowList.get(flowId));
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    };

    return promise;
  },

  setFlowList: (flows) => {
    let _flowList = filterFlows(flows);
    setState(() => {
      return {
        flowList: _flowList
      };
    });
  },

  getFlowList() {
    if (getState().flowList.size) {
      let allFlows = getState().flowList;
      return Promise.resolve(allFlows);
    }
    return fetchPermissibleList()
      .then(() => {
        return Promise.resolve(getState().flowList);
      })
      .catch((err) => {
        return Promise.reject(new Map());
      });
  },

  setExplorerFlows: (flows) => {
    let _flowList = filterFlows(flows);
    setState(() => {
      return {
        explorerFlows: _flowList
      };
    });
  },

  setFilteredFlows: (flow) => {
    setState(() => {
      return {
        explorerFilteredFlows: flow
      };
    });
  },

  setCreateAllowedFlow: (flow) => {
    setState(() => {
      return {
        createAllowedFlows: flow
      };
    });
  },

  setTemplateAllowedFlow: (flow) => {
    setState(() => {
      return {
        templateAllowedFlows: flow
      };
    });
  },

  setArchivedFlows: (flows) => {
    setState(() => {
      return {
        archivedFlows: flows
      };
    });
  },

  setExplorerFlowsLoading: (isLoading) => {
    setState(() => {
      return {
        isExplorerFlowsLoading: isLoading
      };
    });
  },

  updateExplorerFlows: () => {
    const { _id: accountId } = useAccountStore.getState();
    const [getList] = DataService.post(
      `/flow/2/${accountId}/explore/?exclude_app_flows=true`
    );
    getList().then((resp) => {
      getState().setExplorerFlows(resp);
    });
  },

  reset: () => {
    setState(() => {
      return { ...INITIAL_STORE };
    });
  }
}));

export { useGlobalStore, LEFTNAV_ACTION };

function filterFlows(data) {
  const accountId = useAccountStore.getState()._id;
  const currentEnv =
    useUserStore.getState()?.sandbox?.find((env) => env._id === accountId) ||
    {};
  const filterProducts = [];
  if (!currentEnv?.Type || currentEnv?.Type === ENVIRONMENT_TYPES.PRODUCTION) {
    filterProducts.push(
      ...[
        PRODUCTS.PROCESS,
        PRODUCTS.CASE,
        PRODUCTS.APPLICATION,
        PRODUCTS.PORTAL,
        PRODUCTS.DATASET,
        PRODUCTS.LIST,
        PRODUCTS.INTEGRATION,
        PRODUCTS.PROJECT
      ]
    );
  } else {
    filterProducts.push(
      PRODUCTS.APPLICATION,
      PRODUCTS.PORTAL,
      PRODUCTS.DATASET
    );
  }
  let _flowList = new Map();
  data?.forEach((flow) => {
    if (flow["_application_id"] || filterProducts.includes(flow?.Type)) {
      _flowList.set(flow._id, flow);
    }
  });
  return _flowList;
}

// Note: To avoid multiple Permissible call
function permissibleFlowsSingletonFactory() {
  let instance;

  return () => {
    if (instance) return instance;

    const accountId = useAccountStore.getState()._id;
    let [fetchFlows] = DataService.post(`/flow/2/${accountId}/permissible`, {
      query: { include_app_flows: true, include_child_flows: true },
      data: getFilterPayload(),
      removeAppIdQuery: true
    });
    return (instance ||= fetchFlows().then((allFlowsList) => {
      useGlobalStore.getState().setFlowList(allFlowsList);
    }));
  };
}
